<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch } from 'vue';
import { IconAdd, IconDelete, IconEdit, IconSound } from '@/components/design/icons';
import type { ChangelogInDb } from '@/api';
import HtmlRenderer from '@/components/HtmlRenderer.vue';
import {
  createChangelogEntry,
  deleteChangelogEntry,
  fetchChangelogs,
  updateChangelogEntry,
} from '@/services/changelogService';
import { useAuthStore } from '@/stores/auth';
import { useChangelogStore } from '@/stores/changelogStore';
import { formatDateForDisplay } from '@/utils/datetimeUtils';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

// Async so CKEditor stays out of the main bundle — the modal is mounted
// globally but only platform admins ever reach edit mode.
const CkEditorInput = defineAsyncComponent(() => import('@/components/CkeditorInput.vue'));

const INITIAL_COUNT = 20;
const PAGE_SIZE = 10;

const props = defineProps<{
  show: boolean;
  /** Already-fetched entries (e.g. the boot check's "new since last seen"). */
  changelogs?: ChangelogInDb[];
}>();

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
}>();

const authStore = useAuthStore();
const changelogStore = useChangelogStore();
const message = useGaiaToast();

const isAdmin = computed(() => authStore.userPermissions.is_platform_admin);

const entries = ref<ChangelogInDb[]>([]);
// Global total from the backend; null until the first unfiltered fetch, so
// a prefetch-seeded list keeps scrolling until the backend tells us the total.
const totalCount = ref<number | null>(null);
const loading = ref(false);
const selectedUid = ref<string | null>(null);

const mode = ref<'view' | 'edit' | 'create'>('view');
const draftTitle = ref('');
const draftHtml = ref('');
const saving = ref(false);
const deleting = ref(false);

const selectedEntry = computed(() => entries.value.find(e => e.uid === selectedUid.value) ?? null);
const finished = computed(
  () => totalCount.value !== null && entries.value.length >= totalCount.value
);

async function loadMore(): Promise<void> {
  if (loading.value || finished.value) return;
  loading.value = true;
  try {
    // Fill up to the initial page first (prefetched entries may be fewer),
    // then continue in smaller pages. Prefetched "since" entries are a strict
    // prefix of the newest-first global list, so offset continuation is safe.
    const limit =
      entries.value.length < INITIAL_COUNT ? INITIAL_COUNT - entries.value.length : PAGE_SIZE;
    const result = await fetchChangelogs({ offset: entries.value.length, limit });
    if (!result) return;
    totalCount.value = result.result_count;
    const known = new Set(entries.value.map(e => e.uid));
    entries.value = [...entries.value, ...result.results.filter(e => !known.has(e.uid))];
    if (!selectedUid.value && entries.value.length > 0) {
      selectedUid.value = entries.value[0].uid;
    }
  } finally {
    loading.value = false;
  }
}

function selectEntry(uid: string): void {
  mode.value = 'view';
  selectedUid.value = uid;
}

function startCreate(): void {
  draftTitle.value = '';
  draftHtml.value = '';
  mode.value = 'create';
}

function startEdit(): void {
  if (!selectedEntry.value) return;
  draftTitle.value = selectedEntry.value.title ?? '';
  draftHtml.value = selectedEntry.value.html;
  mode.value = 'edit';
}

function cancelEdit(): void {
  mode.value = 'view';
}

async function save(): Promise<void> {
  if (!draftHtml.value.trim()) return;
  saving.value = true;
  try {
    if (mode.value === 'create') {
      const created = await createChangelogEntry({
        title: draftTitle.value.trim() || null,
        html: draftHtml.value,
      });
      if (!created) {
        message.error('Failed to create the changelog entry');
        return;
      }
      entries.value = [created, ...entries.value];
      if (totalCount.value !== null) totalCount.value += 1;
      selectedUid.value = created.uid;
    } else {
      const current = selectedEntry.value;
      if (!current) return;
      const updated = await updateChangelogEntry(current.uid, {
        title: draftTitle.value.trim() || null,
        html: draftHtml.value,
      });
      if (!updated) {
        message.error('Failed to update the changelog entry');
        return;
      }
      entries.value = entries.value.map(e => (e.uid === updated.uid ? updated : e));
    }
    mode.value = 'view';
  } finally {
    saving.value = false;
  }
}

async function removeEntry(): Promise<void> {
  const current = selectedEntry.value;
  if (!current) return;
  deleting.value = true;
  try {
    const deleted = await deleteChangelogEntry(current.uid);
    if (!deleted) {
      message.error('Failed to delete the changelog entry');
      return;
    }
    entries.value = entries.value.filter(e => e.uid !== current.uid);
    if (totalCount.value !== null) totalCount.value -= 1;
    selectedUid.value = entries.value[0]?.uid ?? null;
  } finally {
    deleting.value = false;
  }
}

watch(
  () => props.show,
  async show => {
    if (!show) return;
    mode.value = 'view';
    entries.value = props.changelogs ? [...props.changelogs] : [];
    totalCount.value = null;
    selectedUid.value = entries.value[0]?.uid ?? null;
    if (entries.value.length < INITIAL_COUNT) {
      await loadMore();
    }
    changelogStore.markSeen(entries.value[0]?.created_at);
  }
);
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    title="Changelog"
    style="width: 900px; max-width: 92vw"
    @update:show="emit('update:show', $event)"
  >
    <n-flex :wrap="false" style="height: 65vh">
      <n-flex vertical :size="8" style="width: 280px; flex-shrink: 0">
        <n-button v-if="isAdmin" dashed block size="small" @click="startCreate">
          <template #icon>
            <n-icon :component="IconAdd" />
          </template>
          New entry
        </n-button>
        <n-infinite-scroll
          style="flex: 1; min-height: 0"
          :distance="20"
          :finished="finished"
          @load="loadMore"
        >
          <n-timeline style="padding: 4px 8px">
            <n-timeline-item
              v-for="entry in entries"
              :key="entry.uid"
              :type="entry.uid === selectedUid ? 'info' : 'default'"
            >
              <template #icon>
                <n-icon :component="IconSound" />
              </template>
              <div
                class="changelog-timeline-entry"
                :class="{ selected: entry.uid === selectedUid }"
                @click="selectEntry(entry.uid)"
              >
                <div class="entry-date">{{ formatDateForDisplay(entry.created_at) }}</div>
                <div v-if="entry.title" class="entry-title">{{ entry.title }}</div>
              </div>
            </n-timeline-item>
          </n-timeline>
          <n-spin v-if="loading" size="small" style="display: block; margin: 8px auto" />
        </n-infinite-scroll>
      </n-flex>
      <n-divider vertical style="height: auto" />
      <n-flex vertical :size="8" style="flex: 1; min-width: 0">
        <template v-if="mode === 'view'">
          <n-flex v-if="isAdmin && selectedEntry" justify="end" :size="4">
            <n-button text size="small" @click="startEdit">
              <template #icon>
                <n-icon :component="IconEdit" />
              </template>
              Edit
            </n-button>
            <n-popconfirm @positive-click="removeEntry">
              <template #trigger>
                <n-button text size="small" type="error" :loading="deleting">
                  <template #icon>
                    <n-icon :component="IconDelete" />
                  </template>
                  Delete
                </n-button>
              </template>
              Delete this changelog entry?
            </n-popconfirm>
          </n-flex>
          <n-scrollbar style="flex: 1; min-height: 0">
            <html-renderer v-if="selectedEntry" :content="selectedEntry.html" />
            <n-empty v-else description="No changelog entries" style="margin-top: 48px" />
          </n-scrollbar>
        </template>
        <template v-else>
          <gaia-input v-model:value="draftTitle" placeholder="Title (optional)" />
          <div class="changelog-editor">
            <ck-editor-input v-model="draftHtml" extended-mode />
          </div>
          <n-flex justify="end" :size="8">
            <n-button @click="cancelEdit">Cancel</n-button>
            <n-button type="primary" :loading="saving" :disabled="!draftHtml.trim()" @click="save">
              {{ mode === 'create' ? 'Create' : 'Save' }}
            </n-button>
          </n-flex>
        </template>
      </n-flex>
    </n-flex>
  </n-modal>
</template>

<style scoped>
.changelog-timeline-entry {
  cursor: pointer;
}

.entry-date {
  font-size: 12px;
  opacity: 0.7;
}

.entry-title {
  font-size: 14px;
}

.changelog-timeline-entry.selected .entry-date,
.changelog-timeline-entry.selected .entry-title {
  color: var(--primary-color);
  font-weight: 600;
}

.changelog-editor {
  flex: 1;
  min-height: 0;
}

/* CkeditorInput forces a fixed 200px content height (inline styles from JS);
   here the editor should fill the remaining pane height and scroll inside. */
.changelog-editor :deep(.ck-editor) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.changelog-editor :deep(.ck-editor__main) {
  flex: 1;
  min-height: 0;
}

.changelog-editor :deep(.ck-editor__editable) {
  height: 100% !important;
  min-height: 0 !important;
  overflow-y: auto;
  box-sizing: border-box;
}
</style>
